import { useMemo } from 'react';
import { Alert, CircularProgress, Divider, Stack } from '@mui/material';

import { GROUP_PANEL_TASK_STATUSES, Task, TaskGroupSummary, TaskStatuses } from '../../types';
import TaskRow from '../../TaskRow';
import CompletedTaskRow from '../CompletedTaskRow';

import useMemberTasks from 'services/memberTasks/queries/useMemberTasks';

interface GroupTaskListProps {
    group: TaskGroupSummary;
    /**
     * Optional client-side filter for the fetched child tasks (defaults to showing all). Permanent
     * groups like monthly reimbursement use it to drop long-since-completed rows that would otherwise
     * pile up, while keeping active and recently-completed tasks.
     */
    filterTasks?: (task: Task) => boolean;
}

/**
 * Default body for an `expand` group drawer: the group's child tasks fetched lazily, rendered as
 * TaskRows in a divided list — the whole row opens the task. Completed tasks persist as a
 * non-interactive CompletedTaskRow. Custom groups supply their own body instead of this.
 */
export default function GroupTaskList({ group, filterTasks }: Readonly<GroupTaskListProps>) {
    const { data, isLoading, isError } = useMemberTasks({ groupId: group.id, statuses: GROUP_PANEL_TASK_STATUSES });

    const tasks = useMemo(() => {
        if (!data) {
            return data;
        }
        return filterTasks ? data.filter(filterTasks) : data;
    }, [data, filterTasks]);

    // Tag each child's tracking events (incl. completion) with the parent group's context.
    const trackingProperties = useMemo(
        () => ({ in_group: true, group_id: group.id, group_template_name: group.group_template.name }),
        [group.id, group.group_template.name]
    );

    if (isLoading) {
        return (
            <Stack sx={{ alignItems: 'center', py: 4 }}>
                <CircularProgress />
            </Stack>
        );
    }

    if (isError) {
        return <Alert severity="error">We couldn&apos;t load these tasks. Please try again.</Alert>;
    }

    if (!tasks || tasks.length === 0) {
        return null;
    }

    return (
        <Stack divider={<Divider />}>
            {tasks.map((task) =>
                task.status === TaskStatuses.COMPLETED ? (
                    <CompletedTaskRow key={task.id} task={task} />
                ) : (
                    <TaskRow key={task.id} task={task} trackingProperties={trackingProperties} />
                )
            )}
        </Stack>
    );
}
